// Estado do construtor de e-mails: modelo, seleção, histórico (undo/redo),
// autosave e preview renderizado no SERVIDOR (uma única fonte visual). Sem
// Vuex: vive só na tela, como o construtor de Páginas.
import { ref, computed, watch } from 'vue';
import API from 'dashboard/api/marketingEmailTemplates';

const HISTORY_LIMIT = 50;
export const PREVIEW_DELAY = 500;
export const SAVE_DELAY = 1500;
export const FIXED_KINDS = ['header', 'signature', 'footer'];

const debounce = (fn, ms) => {
  let timer = null;
  const wrapped = (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
  wrapped.cancel = () => clearTimeout(timer);
  return wrapped;
};

export const newBlockId = () =>
  `blk_${Math.random().toString(16).slice(2, 14)}`;

// Espelho do EmailBuilder::BlockCatalog.default_style (o servidor revalida).
export const defaultStyle = kind => ({
  background: kind === 'footer' ? 'surface' : 'white',
  align: ['hero', 'button', 'event'].includes(kind) ? 'center' : 'left',
  spacing: 'comfortable',
});

export const buildBlock = (catalog, kind, variant = null) => {
  const variants = catalog?.variants?.[kind] || [];
  return {
    id: newBlockId(),
    kind,
    version: catalog?.version || 1,
    variant: variants.includes(variant) ? variant : variants[0],
    style: defaultStyle(kind),
    ...JSON.parse(JSON.stringify(catalog?.defaults?.[kind] || {})),
  };
};

export function useEmailBuilder({ templateId, onError } = {}) {
  const template = ref(null);
  const catalog = ref(null);
  const loading = ref(true);
  const selectedId = ref(null);
  const device = ref('desktop');
  const dark = ref(false);
  const saveState = ref('saved'); // saved | dirty | saving | error
  const preview = ref({ html: '', text: '', sizeKb: 0, loading: false });
  const undoStack = ref([]);
  const redoStack = ref([]);
  let lastSnapshot = null;
  let suspendHistory = false;

  const blocks = computed(() => template.value?.blocks || []);
  const selectedBlock = computed(() =>
    blocks.value.find(b => b.id === selectedId.value)
  );
  const isPersonal = computed(() => template.value?.mode === 'personal');
  const canUndo = computed(() => undoStack.value.length > 0);
  const canRedo = computed(() => redoStack.value.length > 0);

  // Só o que o servidor precisa pra renderizar/salvar.
  const source = () => ({
    mode: template.value.mode,
    blocks: template.value.blocks,
    settings: template.value.settings || {},
    personal_text: template.value.personal_text || '',
    subject: template.value.subject || '',
  });

  const snapshot = () =>
    JSON.stringify({
      blocks: template.value.blocks,
      settings: template.value.settings,
      personal_text: template.value.personal_text,
      subject: template.value.subject,
      mode: template.value.mode,
    });

  const restore = snap => {
    suspendHistory = true;
    Object.assign(template.value, JSON.parse(snap));
    lastSnapshot = snap;
    suspendHistory = false;
  };

  const recordHistory = () => {
    if (suspendHistory) return;
    const snap = snapshot();
    if (snap === lastSnapshot) return;
    if (lastSnapshot) {
      undoStack.value.push(lastSnapshot);
      if (undoStack.value.length > HISTORY_LIMIT) undoStack.value.shift();
    }
    redoStack.value = [];
    lastSnapshot = snap;
  };
  const debouncedHistory = debounce(recordHistory, 400);

  const undo = () => {
    if (!undoStack.value.length) return;
    redoStack.value.push(snapshot());
    restore(undoStack.value.pop());
    saveState.value = 'dirty';
  };
  const redo = () => {
    if (!redoStack.value.length) return;
    undoStack.value.push(snapshot());
    restore(redoStack.value.pop());
    saveState.value = 'dirty';
  };

  // ---- preview (servidor) ----
  const refreshPreview = async () => {
    if (!template.value) return;
    preview.value.loading = true;
    try {
      const { data } = await API.preview(source(), { dark: dark.value });
      preview.value = {
        html: data.html,
        text: data.text,
        sizeKb: data.size_kb,
        loading: false,
      };
    } catch (error) {
      preview.value.loading = false;
      onError?.(error);
    }
  };
  const schedulePreview = debounce(refreshPreview, PREVIEW_DELAY);

  // ---- save ----
  const save = async () => {
    if (!template.value) return false;
    saveState.value = 'saving';
    try {
      const { data } = await API.update(template.value.id, {
        marketing_email_template: {
          name: template.value.name,
          subject: template.value.subject,
          mode: template.value.mode,
          personal_text: template.value.personal_text,
          blocks: template.value.blocks,
          settings: template.value.settings,
        },
      });
      // O servidor normaliza os blocos (ids, variantes, mídia): reflete.
      suspendHistory = true;
      template.value.blocks = data.blocks;
      template.value.settings = data.settings;
      template.value.theme = data.theme;
      template.value.body_html = data.body_html;
      suspendHistory = false;
      lastSnapshot = snapshot();
      saveState.value = 'saved';
      return true;
    } catch (error) {
      saveState.value = 'error';
      onError?.(error);
      return false;
    }
  };
  const scheduleSave = debounce(save, SAVE_DELAY);

  const touch = () => {
    saveState.value = 'dirty';
    debouncedHistory();
    schedulePreview();
    scheduleSave();
  };

  // ---- edições ----
  const updateTemplate = patch => {
    Object.assign(template.value, patch);
    touch();
  };
  const updateSettings = patch => {
    template.value.settings = { ...(template.value.settings || {}), ...patch };
    touch();
  };
  const updateTheme = patch => {
    const theme = {
      ...(template.value.settings?.theme || template.value.theme || {}),
      ...patch,
    };
    updateSettings({ theme });
    template.value.theme = { ...(template.value.theme || {}), ...patch };
  };
  const updateBlock = (id, patch) => {
    const idx = blocks.value.findIndex(b => b.id === id);
    if (idx < 0) return;
    template.value.blocks.splice(idx, 1, { ...blocks.value[idx], ...patch });
    touch();
  };
  const updateSelected = patch =>
    selectedId.value && updateBlock(selectedId.value, patch);

  // Bloco novo entra depois do selecionado; nunca depois do rodapé.
  const insertIndex = () => {
    const list = blocks.value;
    const sel = list.findIndex(b => b.id === selectedId.value);
    const footer = list.findIndex(b => b.kind === 'footer');
    const sig = list.findIndex(b => b.kind === 'signature');
    const end = [footer, sig].filter(i => i >= 0);
    const limit = end.length ? Math.min(...end) : list.length;
    if (sel >= 0 && sel + 1 <= limit) return sel + 1;
    return limit;
  };
  const addBlock = (kind, variant = null) => {
    const block = buildBlock(catalog.value, kind, variant);
    template.value.blocks.splice(insertIndex(), 0, block);
    selectedId.value = block.id;
    touch();
    return block;
  };
  const removeBlock = id => {
    const idx = blocks.value.findIndex(b => b.id === id);
    if (idx < 0) return;
    template.value.blocks.splice(idx, 1);
    if (selectedId.value === id) selectedId.value = null;
    touch();
  };
  const duplicateBlock = id => {
    const idx = blocks.value.findIndex(b => b.id === id);
    if (idx < 0) return;
    const copy = {
      ...JSON.parse(JSON.stringify(blocks.value[idx])),
      id: newBlockId(),
    };
    template.value.blocks.splice(idx + 1, 0, copy);
    selectedId.value = copy.id;
    touch();
  };
  const moveBlock = (id, direction) => {
    const idx = blocks.value.findIndex(b => b.id === id);
    const to = idx + direction;
    if (idx < 0 || to < 0 || to >= blocks.value.length) return;
    const [blk] = template.value.blocks.splice(idx, 1);
    template.value.blocks.splice(to, 0, blk);
    touch();
  };

  // Resultado da IA (compor/editar): aplica de uma vez, com histórico.
  const applyAiResult = result => {
    if (result.subject) template.value.subject = result.subject;
    if (result.preheader) {
      template.value.settings = {
        ...(template.value.settings || {}),
        preheader: result.preheader,
      };
    }
    if (result.mode === 'personal') {
      template.value.personal_text = result.personal_text;
    } else if (Array.isArray(result.blocks) && result.blocks.length) {
      template.value.blocks = result.blocks;
    }
    selectedId.value = null;
    // Histórico na hora (não debounced): um "desfazer" volta o e-mail inteiro.
    recordHistory();
    saveState.value = 'dirty';
    schedulePreview();
    scheduleSave();
  };

  const load = async () => {
    loading.value = true;
    try {
      const [{ data: tpl }, { data: cat }] = await Promise.all([
        API.show(templateId),
        API.catalog(),
      ]);
      template.value = tpl;
      catalog.value = cat;
      lastSnapshot = snapshot();
      await refreshPreview();
    } catch (error) {
      onError?.(error);
    } finally {
      loading.value = false;
    }
  };

  watch(dark, () => schedulePreview());

  const flush = async () => {
    scheduleSave.cancel();
    if (saveState.value === 'dirty' || saveState.value === 'error')
      await save();
  };

  return {
    template,
    catalog,
    loading,
    selectedId,
    selectedBlock,
    blocks,
    isPersonal,
    device,
    dark,
    saveState,
    preview,
    canUndo,
    canRedo,
    load,
    save,
    flush,
    undo,
    redo,
    refreshPreview,
    source,
    updateTemplate,
    updateSettings,
    updateTheme,
    updateBlock,
    updateSelected,
    addBlock,
    removeBlock,
    duplicateBlock,
    moveBlock,
    applyAiResult,
  };
}
